import type { LoginFormValues } from "./schema";
import { mapLoginError } from "@/lib/login-error-mapping";
import { landingHref } from "@/types/employees";
import type { AuthMePayload } from "@/types/auth";

/**
 * @MX:ANCHOR: SPEC-011 REQ-010/REQ-011/REQ-013 login submit pipeline.
 * @MX:REASON: Pure DI function so AC-LOGIN-3/4 (csrf retry) become Vitest unit-tests without rendering UI.
 */
export interface OnSubmitDeps {
  fetch: typeof globalThis.fetch;
  getCsrfCookieValue: (cookieString?: string) => string | undefined;
  bootstrapCsrf: () => Promise<void>;
  navigate: (path: string) => void;
  setError: (message: string) => void;
}

async function postLogin(
  deps: OnSubmitDeps,
  values: LoginFormValues,
  csrfToken: string | undefined,
): Promise<Response> {
  return deps.fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ ...values, csrf_token: csrfToken }),
  });
}

async function readDetail(response: Response): Promise<string | undefined> {
  try {
    const parsed = (await response.json()) as { detail?: string };
    return parsed?.detail;
  } catch {
    return undefined;
  }
}

// SPEC-092 REQ-UI-003: вести вошедшего на ПЕРВЫЙ доступный ему экран, а не всегда
// на /retail. Права приходят прямо в теле ответа login (UserOut.operations);
// владелец/админ → operations null/absent → landingHref вернёт /retail (как было).
// Если тело не читается — безопасный fallback /retail.
async function landingFor(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as AuthMePayload;
    const operations =
      body && Array.isArray(body.operations) ? body.operations : null;
    return landingHref(operations);
  } catch {
    return "/retail";
  }
}

// SPEC-092 D15: при 429 ограничитель входа шлёт Retry-After; протащим его в
// сообщение об ошибке.
function retryAfterOf(response: Response): number | undefined {
  const raw = Number(response.headers.get("Retry-After"));
  return Number.isFinite(raw) && raw > 0 ? raw : undefined;
}

export async function loginSubmit(
  values: LoginFormValues,
  deps: OnSubmitDeps,
): Promise<void> {
  // (a) ensure csrf cookie present
  let csrf = deps.getCsrfCookieValue();
  if (!csrf) {
    try {
      await deps.bootstrapCsrf();
    } catch {
      deps.setError(mapLoginError({ networkFailure: true }).message);
      return;
    }
    csrf = deps.getCsrfCookieValue();
  }

  // (b) first POST attempt
  let response: Response;
  try {
    response = await postLogin(deps, values, csrf);
  } catch {
    deps.setError(mapLoginError({ networkFailure: true }).message);
    return;
  }

  if (response.status === 200) {
    deps.navigate(await landingFor(response));
    return;
  }

  // (c) REQ-013: one CSRF retry on 403
  if (response.status === 403) {
    try {
      await deps.bootstrapCsrf();
    } catch {
      deps.setError(mapLoginError({ status: 403 }).message);
      return;
    }
    const csrf2 = deps.getCsrfCookieValue();
    let retry: Response;
    try {
      retry = await postLogin(deps, values, csrf2);
    } catch {
      deps.setError(mapLoginError({ networkFailure: true }).message);
      return;
    }
    if (retry.status === 200) {
      deps.navigate(await landingFor(retry));
      return;
    }
    const detail = await readDetail(retry);
    deps.setError(
      mapLoginError({
        status: retry.status,
        detail,
        retryAfter: retryAfterOf(retry),
      }).message,
    );
    return;
  }

  // (d) all other non-200 → map error (429 несёт Retry-After — SPEC-092 D15)
  const detail = await readDetail(response);
  deps.setError(
    mapLoginError({
      status: response.status,
      detail,
      retryAfter: retryAfterOf(response),
    }).message,
  );
}
